import { useRef, type MouseEvent, type PointerEvent } from 'react';

/** Native text dragging and touch scrolling must not become record opens. */
export function useRowPointerGesture() {
  const gesture = useRef<
    { pointerId: number; x: number; y: number; moved: boolean } | undefined
  >(undefined);
  const trackMovement = (event: PointerEvent<HTMLElement>) => {
    const start = gesture.current;
    if (
      start &&
      start.pointerId === event.pointerId &&
      Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8
    )
      start.moved = true;
  };
  return {
    wasDragged: () => !!gesture.current?.moved,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (event.button === 0 && event.isPrimary !== false)
          gesture.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            moved: false,
          };
      },
      onPointerMove: trackMovement,
      onPointerUp: trackMovement,
      onPointerCancel: () => {
        if (gesture.current) gesture.current.moved = true;
      },
    },
  };
}

/** Selecting a title should copy text without opening its record. */
export function hasRowTextSelection(row: HTMLElement): boolean {
  const selection = row.ownerDocument.getSelection();
  if (!selection || selection.isCollapsed) return false;
  for (let index = 0; index < selection.rangeCount; index++) {
    if (selection.getRangeAt(index).intersectsNode(row)) return true;
  }
  return false;
}

/** Pointer shortcuts must leave text selection and modifier gestures alone. */
export function isRowPointerClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.button === 0 &&
    event.detail > 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !hasRowTextSelection(event.currentTarget)
  );
}

/** Keep native controls in a table row independent of its pointer shortcut. */
export function isRowOpenClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    isRowPointerClick(event) &&
    event.target instanceof Element &&
    event.currentTarget.contains(event.target) &&
    !event.target.closest(
      'button, a, input, select, textarea, summary, [role="menuitem"], [contenteditable], [data-row-actions]',
    )
  );
}
